import React, { useState, useEffect } from "react";
import api from "../services/api";
import { Trophy, Sparkles, Flame, User, Medal, Zap, Loader2, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../services/auth";
import toast, { Toaster } from 'react-hot-toast';

export default function FriendCompetition() {
  const [userRole, setUserRole] = useState("student");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("global");

  const categories = [
    { id: "global", label: "🌐 Global", desc: "All Students Month" },
    { id: "class", label: "🏫 Classmates", desc: "Class School Ranking" },
    { id: "weekly", label: "⏱️ Weekly Reset", desc: "Fresh Batch This Week" },
    { id: "subject", label: "📚 Subject Master", desc: "Curriculum Topper" },
    { id: "coding", label: "💻 Coding Elite", desc: "Problems Solved & Speed" },
  ];

  const avatarList = ["🚀", "🦊", "🦖", "🐼"];

  useEffect(() => {
    try {
      const token = getToken();
      if (token) setUserRole(jwtDecode(token).role || "student");
    } catch (err) {}
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [activeTab]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/gamification/leaderboard?type=${activeTab}`);
      setLeaderboard(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Classroom feed currently offline.");
    } finally {
      setLoading(false);
    }
  };

  // Separate Podium (1, 2, 3) vs general list
  const topThree = leaderboard.slice(0, 3);
  const remainder = leaderboard.slice(3);

  // Rearrange top three for visual podium layout: [2nd, 1st, 3rd]
  const podiumLayout = [];
  if (topThree[1]) podiumLayout.push({ ...topThree[1], pos: 2, height: 'h-44', order: 'order-1', bg: 'bg-slate-100 border-slate-300', text: 'text-slate-500', label: '2nd Place' });
  if (topThree[0]) podiumLayout.push({ ...topThree[0], pos: 1, height: 'h-56', order: 'order-2', bg: 'bg-yellow-100 border-yellow-400', text: 'text-yellow-600', label: 'Grand Master' });
  if (topThree[2]) podiumLayout.push({ ...topThree[2], pos: 3, height: 'h-36', order: 'order-3', bg: 'bg-amber-100 border-amber-300', text: 'text-amber-700', label: '3rd Place' });

  return (
    <div className="flex h-screen bg-[#FAFCFF] dark:bg-slate-950 overflow-hidden relative font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <main className="flex-1 flex flex-col h-full relative overflow-y-auto pb-20 z-10 min-w-0">
        
        {/* Deep aesthetic gradients */}
        <div className="absolute top-[-10%] right-[-10%] w-[40vw] h-[40vw] bg-purple-400/10 rounded-full blur-[120px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-[-10%] left-[10%] w-[30vw] h-[30vw] bg-sky-400/10 rounded-full blur-[100px] pointer-events-none" />

        <header className="px-8 pt-8 pb-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 relative z-20 flex-shrink-0 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-slate-950 dark:text-indigo-400 tracking-tight flex items-center gap-2">
                Classroom Tournament Hub <ShieldCheck size={24} className="text-indigo-600 fill-indigo-100 animate-pulse" />
              </h1>
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Compete with classmates globally, earn points, and own the leaderboard!</p>
            </div>
          </div>

          {/* Dynamic Horizontal Tab Bar */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none no-scrollbar -mx-2 px-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`px-5 py-2.5 rounded-2xl border text-xs font-black uppercase tracking-wider transition-all shrink-0 whitespace-nowrap ${
                  activeTab === cat.id
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200 dark:shadow-none scale-105'
                    : 'bg-white dark:bg-slate-900 text-slate-600 border-slate-100 hover:border-indigo-100 dark:border-slate-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <motion.div animate={{rotate: 360}} transition={{repeat:Infinity, duration: 1.2, ease: "linear"}} className="w-14 h-14 border-8 border-indigo-100 border-t-indigo-600 rounded-full" />
            <p className="text-sm font-black text-indigo-700 italic">Synchronizing class roster scorecards...</p>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <Trophy className="text-slate-200 mx-auto mb-4 animate-bounce" size={70} />
            <h3 className="text-xl font-black text-slate-700 dark:text-slate-300">Tournament Ready to Start!</h3>
            <p className="text-sm font-bold text-slate-400 mt-2">Do some lessons and quizzes to become the first player on the board!</p>
          </div>
        ) : (
          <div className="flex-1 p-6 md:p-10 max-w-4xl mx-auto w-full space-y-12">
            
            {/* Podium Showcase */}
            {podiumLayout.length > 0 && (
              <div className="flex flex-col items-center">
                <span className="px-4 py-1.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 text-xs font-black rounded-full border border-indigo-100 dark:border-indigo-900 uppercase tracking-widest mb-8 flex items-center gap-1.5">
                  <Sparkles size={12} fill="currentColor" /> Top Academics This Week
                </span>
                
                <div className="flex items-end justify-center w-full gap-4 md:gap-8 relative">
                  {podiumLayout.map((player, idx) => {
                    const avatarIdx = parseInt(player.profile_picture || "0", 10);
                    const icon = avatarList[avatarIdx] || "🚀";

                    return (
                      <motion.div 
                        key={idx}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: player.pos * 0.1 }}
                        className={`flex flex-col items-center ${player.order} w-24 md:w-36 relative group`}
                      >
                        {/* Avatar & Crown */}
                        <div className="relative mb-4">
                          {player.pos === 1 && (
                            <motion.div 
                              animate={{ y: [0, -5, 0], rotate: [0, 5, -5, 0] }} 
                              transition={{ repeat: Infinity, duration: 3 }} 
                              className="absolute top-[-20px] left-1/2 transform -translate-x-1/2 text-3xl drop-shadow"
                            >
                              👑
                            </motion.div>
                          )}
                          <div className={`w-16 h-16 md:w-20 md:h-20 bg-white border-4 ${player.is_me ? 'border-indigo-600 scale-110 shadow-xl shadow-indigo-200' : 'border-slate-100 shadow-md'} rounded-[1.5rem] flex items-center justify-center text-3xl md:text-4xl shrink-0 relative overflow-hidden`}>
                            {icon}
                            {player.is_me && <span className="absolute bottom-0 left-0 right-0 bg-indigo-600 text-white text-[8px] font-black uppercase py-0.5 text-center tracking-widest">You</span>}
                          </div>
                        </div>

                        <p className="text-xs font-black text-slate-800 dark:text-white tracking-tight truncate w-full text-center px-2 mb-2">
                          {player.name}
                        </p>

                        {/* Physical Podium Block */}
                        <div className={`w-full ${player.height} rounded-t-[2rem] ${player.bg} border-b-0 border-4 p-4 flex flex-col items-center justify-between shadow-xl relative overflow-hidden`}>
                          <div className="absolute top-0 left-0 right-0 bottom-0 bg-[linear-gradient(45deg,rgba(255,255,255,.05)_25%,transparent_25%)] bg-[length:15px_15px]" />
                          <div className="relative flex flex-col items-center text-center gap-1">
                            <span className={`text-2xl md:text-4xl font-black ${player.text}`}>{player.pos}</span>
                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">{player.label}</span>
                          </div>

                          <div className="relative flex flex-col items-center gap-0.5">
                            {activeTab === "coding" ? (
                              <span className="text-xs font-black text-slate-800 dark:text-slate-700 flex items-center gap-0.5"><ShieldCheck size={10} className="text-emerald-600" /> {player.coding_solved || 0} Solved</span>
                            ) : (
                              <span className="text-xs font-black text-slate-800 dark:text-slate-700 flex items-center gap-0.5"><Zap size={10} fill="currentColor" /> {player.xp} XP</span>
                            )}
                            {player.streak > 0 && <span className="text-[9px] font-black text-orange-600 flex items-center gap-0.5"><Flame size={9} fill="currentColor" /> {player.streak} Streak</span>}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Remainder Feed */}
            <div className="space-y-4 flex flex-col">
              <h3 className="text-lg font-black text-slate-700 dark:text-slate-300 tracking-tight border-b border-dashed border-slate-200 pb-3">Challenger Board</h3>
              
              {remainder.length === 0 && <p className="text-xs text-slate-400 italic text-center py-4">The ranks below the top three are waiting for you. Make your move!</p>}

              <div className="space-y-3">
                {remainder.map((player, index) => {
                  const avatarIdx = parseInt(player.profile_picture || "0", 10);
                  const icon = avatarList[avatarIdx] || "🚀";

                  return (
                    <motion.div
                      key={player.id}
                      initial={{opacity: 0, x: -10}}
                      animate={{opacity: 1, x: 0}}
                      transition={{delay: index * 0.05}}
                      className={`px-6 py-4 rounded-[2rem] border-b-4 shadow-md flex items-center justify-between transition-all group relative overflow-hidden ${
                        player.is_me 
                          ? 'bg-indigo-50 border-indigo-300 dark:bg-slate-900 dark:border-indigo-900' 
                          : 'bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800 hover:border-indigo-100 hover:bg-indigo-50/10'
                      }`}
                    >
                      <div className="flex items-center gap-5 z-10">
                        {/* Position Label */}
                        <span className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-black text-slate-500 shrink-0">
                          #{player.rank}
                        </span>

                        {/* Pic */}
                        <div className="w-12 h-12 bg-white shadow-sm border border-slate-100 rounded-2xl flex items-center justify-center text-2xl shrink-0">
                          {icon}
                        </div>

                        <div>
                          <h5 className="font-black text-sm text-slate-800 dark:text-white flex items-center gap-2">
                            {player.name}
                            {player.is_me && <span className="px-2 py-0.5 bg-indigo-600 text-white text-[8px] rounded font-black tracking-widest uppercase">YOU</span>}
                          </h5>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Level {player.level} Cadet</p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 md:gap-8 z-10 font-black">
                        {activeTab === "coding" && (
                          <div className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 shrink-0 text-xs">
                            <ShieldCheck size={14} />
                            <span>{player.coding_solved || 0} Solved</span>
                          </div>
                        )}

                        {player.streak > 0 && activeTab !== "coding" && (
                          <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-3 py-1 rounded-full border border-orange-100 shrink-0">
                            <Flame size={14} fill="currentColor" />
                            <span className="text-xs">{player.streak} Day Streak</span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1 text-slate-800 dark:text-slate-200 text-sm w-20 justify-end">
                          <Zap size={14} fill="currentColor" className="text-indigo-500" />
                          <span>{player.xp} XP</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}
